import Clipboard from '@react-native-clipboard/clipboard';
import React from 'react';
import { Share, Text, View } from 'react-native';
import { AppPressable } from '../components/AppPressable';
import { BottomSheet, type BottomSheetPalette } from '../components/BottomSheet';
import { InviteQrCode } from '../components/InviteQrCode';
import { PUBLIC_WEB_ORIGIN } from '../config';
import { useLocale } from '../i18n/LocaleContext';
import type { ChatListStyles } from '../theme/chatListStylesFromPalette';
import { fillTemplate } from '../util/fillTemplate';
import { showToast } from '../util/toastBridge';
import { inviteLink } from './contactsModel';

/**
 * Пригласить по ссылке или QR (аудит удобства 2026-10-04: вариант A плюс приглашение из C).
 *
 * <p>Новичку без друзей раньше было нечем позвать знакомого: «Поделиться профилем» в настройках
 * неактивна без ника (п. 40), а других путей не было. Здесь ссылка есть всегда: с ником — на
 * свою страницу, без ника — общая на приложение, с подсказкой, где ник завести.
 *
 * <p>QR — для того, кто стоит рядом: показать экран быстрее, чем диктовать ник.
 */
export function InviteSheet({
  visible,
  onClose,
  handle,
  styles,
  palette,
}: {
  visible: boolean;
  onClose: () => void;
  handle: string | null | undefined;
  styles: ChatListStyles;
  palette: BottomSheetPalette;
}) {
  const { t } = useLocale();
  const { url, personal } = inviteLink(handle, PUBLIC_WEB_ORIGIN);
  const message = fillTemplate(t('inviteMessage'), { url });

  return (
    <BottomSheet visible={visible} onClose={onClose} palette={palette} title={t('inviteTitle')} contentStyle={{ gap: 12 }}>
      <Text style={styles.hint}>{personal ? t('inviteBody') : t('inviteNoHandleHint')}</Text>
      <View style={styles.qrBox}>
        <InviteQrCode value={url} size={200} accessibilityLabel={t('inviteQrA11y')} />
      </View>
      <Text style={styles.inviteLinkText} selectable>
        {url}
      </Text>
      <View style={styles.stateCardActions}>
        <AppPressable
          style={styles.pillSecondary}
          onPress={() => {
            Clipboard.setString(url);
            showToast({ message: t('inviteCopied') });
          }}
          accessibilityRole="button"
          accessibilityLabel={t('inviteCopy')}>
          <Text style={styles.pillSecondaryText}>{t('inviteCopy')}</Text>
        </AppPressable>
        <AppPressable
          style={styles.pillPrimary}
          onPress={() => {
            // Отказ системного «Поделиться» (в вебе без navigator.share) — не ошибка: рядом
            // кнопка «Скопировать», и человек воспользуется ею.
            void Share.share({ message, url }).catch(() => undefined);
          }}
          accessibilityRole="button"
          accessibilityLabel={t('inviteShare')}>
          <Text style={styles.pillPrimaryText}>{t('inviteShare')}</Text>
        </AppPressable>
      </View>
    </BottomSheet>
  );
}
